<template>
	<SuiSelectList>
		<template #header>
			<SuiSelectListHeaderWithSearch
				:search-query="searchQuery"
				:search-placeholder="$t('user_assign_select_find_user')"
				@close="$emit('toggle-open')"
				@search="searchQuery = $event"
			>
				<template #title>
					{{ ownerTitle }}
				</template>
				<template #close>
					{{ $t('close') }}
				</template>
			</SuiSelectListHeaderWithSearch>
		</template>
		<div
			v-if="potentialOwners.length > 0"
			:class="{
				'user-assign-select__dropdown--disabled': !isModifyEnabled,
			}"
		>
			<SuiSelectListButton
				v-for="user in potentialOwners"
				:key="user.id"
				:aria-selected="entityOwner?.id === user.id"
				:aria-label="
					$t('user_assign_select_user_label', {
						name: user.name,
					})
				"
				:active="entityOwner?.id === user.id"
				class="user-assign-select__user-button"
				@click="$emit('owner-click', user)"
			>
				<SuiAvatar
					size="x-small"
					show-border
					:url="user.profilePicture?.mini"
					:class="{
						'user-assign-select__cupo-avatar':
							user.type === UserType.Cupo,
					}"
					:initials="user.initials ?? ''"
					:color="user.hslColor ?? ''"
				/>
				<p
					:class="{
						'user-assign-select__user-name--inactive':
							user.status === UserStatus.Inactive,
					}"
				>
					{{ user.name }}
				</p>
				<SuiIcon
					v-if="entityOwner?.id === user.id"
					name="icon-tick-16"
					class="user-assign-select__tick"
				/>
			</SuiSelectListButton>
			<UserAssignSelectListSkeleton v-if="loadingUsersAndGroups" />
		</div>
		<UserAssignSelectListSkeleton v-else-if="loadingUsersAndGroups" />
		<SuiSelectListEmpty v-else>
			{{ $t('search.noResults') }}
		</SuiSelectListEmpty>
		<template v-if="isModifyEnabled" #footer>
			<SuiButton variant="primary" @click="$emit('assign-owner')">
				{{ $t('apply') }}
			</SuiButton>
		</template>
	</SuiSelectList>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
	SuiSelectList,
	SuiSelectListHeaderWithSearch,
	SuiSelectListButton,
	SuiSelectListEmpty,
	SuiAvatar,
	SuiIcon,
	SuiButton,
} from '@developers/scoro-ui';
import {
	type UserAssignSelectUserFragment,
	type Project,
	type Contact,
	type Task,
	UserType,
	UserStatus,
	useUserAssignSelectUsersAndGroupsLazyQuery,
	UserAssignSelectUsersAndGroupsDocument,
} from '@/generated/graphql';
import { FILTERED_USER_TYPES } from '../../constants';
import UserAssignSelectListSkeleton from './List/UserAssignSelectListSkeleton.vue';
import { sortUsers } from '../../utils';

export interface UserAssignSelectOwnerDropdownPopperProps {
	isOpen: boolean;
	isModifyEnabled: boolean;
	owner: UserAssignSelectUserFragment | null | undefined;
	entityOwner: UserAssignSelectUserFragment | null | undefined;
	preloadedUsers?: UserAssignSelectUserFragment[];
	enableRoleSelection?: boolean;
	taskId?: Task['id'];
	projectId?: Project['id'];
	contactId?: Contact['id'];
	loadProjectMembers?: boolean;
	ownerTitle?: string;
}

const {
	owner,
	preloadedUsers = [],
	enableRoleSelection = false,
	taskId = undefined,
	projectId = undefined,
	contactId = undefined,
	loadProjectMembers = false,
	ownerTitle = undefined,
	isOpen,
} = defineProps<UserAssignSelectOwnerDropdownPopperProps>();

defineEmits<{
	'toggle-open': [];
	'owner-click': [user: UserAssignSelectUserFragment];
	'assign-owner': [];
}>();

const searchQuery = ref('');
const usersList = ref<UserAssignSelectUserFragment[]>(
	sortUsers(preloadedUsers)
);
const hasLoadedAllUsers = ref(false);

const baseUsersQueryFilters = computed(() => ({
	userTypes: FILTERED_USER_TYPES,
	projectId,
	contactId,
}));

const baseProjectQueryParams = computed(() => ({
	projectParams: {
		projectId: projectId ? Number(projectId) : undefined,
	},
	loadProject: Boolean(loadProjectMembers && projectId),
}));

const {
	load: loadAllUsers,
	loading: loadingUsersAndGroupsQuery,
	onResult: onResultAllUsers,
} = useUserAssignSelectUsersAndGroupsLazyQuery({
	usersListInput: {
		filters: {
			...baseUsersQueryFilters.value,
		},
	},
	rolesListInput: {
		filters: {
			isActive: true,
		},
	},
	loadRoles: enableRoleSelection,
	...baseProjectQueryParams.value,
});

onResultAllUsers((result) => {
	hasLoadedAllUsers.value = true;
	if (result.data?.UsersList) {
		usersList.value = sortUsers(result.data.UsersList);
	}
});

const loadingUsersAndGroups = computed(
	() => loadingUsersAndGroupsQuery.value || !hasLoadedAllUsers.value
);

const loadUsersAndGroups = () => {
	loadAllUsers(UserAssignSelectUsersAndGroupsDocument, {
		usersListInput: {
			filters: { ...baseUsersQueryFilters.value, taskId },
		},
		rolesListInput: {
			filters: {
				isActive: true,
			},
		},
		loadRoles: enableRoleSelection,
		...baseProjectQueryParams.value,
	});
};

watch(
	() => isOpen,
	(open) => {
		if (open) {
			searchQuery.value = '';
			loadUsersAndGroups();
		}
	},
	{ immediate: true }
);

const potentialOwners = computed(() =>
	usersList.value
		.filter(
			(user) =>
				user.type !== UserType.Cupo &&
				(user.status !== UserStatus.Inactive ||
					user.id === owner?.id) &&
				user.name
					.toLowerCase()
					.trim()
					.includes(searchQuery.value.toLowerCase().trim())
		)
		.sort((a, b) =>
			a.id === owner?.id
				? -1
				: b.id === owner?.id
					? 1
					: a.name.localeCompare(b.name)
		)
);
</script>
